/**
 * Test Cases API Service
 *
 * Handles all test case-related API calls
 */

import { AI_OPERATIONS_API_URL } from './config';
import { demoModeManager } from './demoModeManager';

const API_BASE_URL = `${AI_OPERATIONS_API_URL}/genaiops`;
const isDemoActive = (): boolean => demoModeManager.getDemoMode().isDemoMode;

/**
 * Get the selected client ID from localStorage
 * Used by partner/aws/admin users who need to specify which client they're viewing
 */
const getSelectedClientId = (): string | null => {
  return localStorage.getItem('selectedClientId');
};

/**
 * Append client_id to URL if available (for partner/aws/admin users)
 */
const appendClientId = (url: string): string => {
  const clientId = getSelectedClientId();
  if (!clientId) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}client_id=${clientId}`;
};

/**
 * Get authorization headers with JWT token
 */
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

export interface ListTestCasesParams {
  page?: number;
  limit?: number;
  searchTerm?: string;
  difficulty?: string;
  tags?: string;
}

/**
 * List test cases for a dataset
 */
export const listTestCases = async (datasetId: string, params: ListTestCasesParams = {}): Promise<any> => {
  if (isDemoActive()) return { data: [], meta: { page: 1, limit: 50, total: 0, totalPages: 1 } };
  const {
    page = 1,
    limit = 50,
    searchTerm = '',
    difficulty = '',
    tags = ''
  } = params;

  const queryParams = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
    ...(searchTerm && { searchTerm }),
    ...(difficulty && { difficulty }),
    ...(tags && { tags })
  });

  const response = await fetch(
    appendClientId(`${API_BASE_URL}/datasets/${datasetId}/test-cases?${queryParams}`),
    {
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch test cases: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Get a single test case by ID
 */
export const getTestCase = async (testCaseId: string): Promise<any> => {
  if (isDemoActive()) return { id: testCaseId, name: 'Demo Test Case', input: '', expected_output: '' };
  const response = await fetch(appendClientId(`${API_BASE_URL}/test-cases/${testCaseId}`), {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch test case: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Create a new test case
 */
export const createTestCase = async (datasetId: string, testCaseData: Record<string, any>): Promise<any> => {
  if (isDemoActive()) return { id: 'demo-tc-' + Date.now(), dataset_id: datasetId, ...testCaseData };
  const response = await fetch(
    appendClientId(`${API_BASE_URL}/datasets/${datasetId}/test-cases`),
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(testCaseData),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to create test case: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Update an existing test case
 */
export const updateTestCase = async (testCaseId: string, updates: Record<string, any>): Promise<any> => {
  if (isDemoActive()) return { id: testCaseId, ...updates };
  const response = await fetch(appendClientId(`${API_BASE_URL}/test-cases/${testCaseId}`), {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to update test case: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Delete a test case
 */
export const deleteTestCase = async (testCaseId: string): Promise<any> => {
  if (isDemoActive()) return { success: true };
  const response = await fetch(appendClientId(`${API_BASE_URL}/test-cases/${testCaseId}`), {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to delete test case: ${response.statusText}`);
  }

  return response.json();
};
